import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { AuthService, BaseSiteService, OccEndpointsService } from '@spartacus/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, publishReplay, refCount, take, tap } from 'rxjs/operators';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from 'src/app/spartacus/features/asm/asm-constant';
import { DealerPriceCheckerService } from 'src/app/utility/dealer-price-checker.service';
import { BaseSiteConfigService } from '../base-site/base-site-config.service';
import { ComponentService } from '../component.service';
import { ShimanoLanguageService } from '../language/shimano-language.service';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root',
})
export class DealerPriceService {
  occUrl = this.meta.getTag('name=occ-backend-base-url');
  headers = new HttpHeaders().set('Content-Type', 'application/json');
  asmEnabled = this.storageService?.getLocalStorageItem(ASM_ENABLED_LOCAL_STORAGE_KEY);
  cacheWindowTime = 3600000; // 1 hour
  timeLeft: number = 5;

  private cachedDealerPrices$: any = {};

  constructor(
    protected http: HttpClient,
    protected auth: AuthService,
    protected baseService: BaseSiteService,
    protected componentService: ComponentService,
    protected languageService: ShimanoLanguageService,
    protected meta: Meta,
    private occEndpointsService: OccEndpointsService,
    private dealerPriceChecker: DealerPriceCheckerService,
    private baseSiteConfigService: BaseSiteConfigService,
    private storageService: StorageService
  ) {}

  get activeLanguage(): string {
    let activeLanguage = '';
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  getDealerPricesByProductCodes(
    productCodes: string[],
    lang = this.activeLanguage,
    b2bUnit = this.storageService.getLocalStorageItem('shimanoSessionB2BUnit')
  ): Observable<any> {
    if ((productCodes.length === 1 && productCodes[0] === '') || productCodes.length < 1) {
      return of({ dealerPrices: [] });
    }
    const idString = [productCodes.toString(), lang, b2bUnit].join();
    const prodCodes = productCodes;

    const body = {
      productCodes: productCodes,
    };

    const httpOptions = {
      headers: this.headers,
    };

    if (!this.cachedDealerPrices$[idString]) {
      this.cachedDealerPrices$[idString] = this.http
        .post(this.occEndpointsService.buildUrl(`/products/dealerPrices`), JSON.stringify(body), httpOptions)
        .pipe(
          publishReplay(1, this.cacheWindowTime),
          refCount(),
          take(1),
          tap((prod: any) => {
            if (prod.dealerPrices.length <= 0) {
              this.dealerPriceChecker.dealerPriceLoaderStartTimer(this.timeLeft);
            } else {
              prodCodes.forEach((code) => {
                if (prod.dealerPrices.filter((data: any) => data.productCode === code).length < 1) {
                  this.dealerPriceChecker.dealerPriceLoaderStartTimer(this.timeLeft);
                }
              });
            }
          }),
          catchError(() => {
            this.componentService.setIsAddToCartDisabled(true);
            this.dealerPriceChecker.dealerPriceLoaderStartTimer(this.timeLeft);
            return of({ dealerPrices: [] });
          })
        );
    }
    return this.cachedDealerPrices$[idString];
  }

  getDealerPriceByProductCode(dealerPrices: DealerPrice[], productCode: string): string {
    return (
      dealerPrices
        .filter((dealerPrice) => dealerPrice.productCode === productCode)
        .map((res) => res.price.formattedValue)[0] || ''
    );
  }

  getErpPromoByProductCode(dealerPrices: DealerPrice[], productCode: string): string {
    return dealerPrices
      .filter((dealerPrice) => dealerPrice.productCode === productCode)
      .map((res) => res.promotionMessage)[0];
  }

  getDealerPriceValueByProductCode(dealerPrices: DealerPrice[], productCode: string): number {
    return (
      dealerPrices.filter((dealerPrice) => dealerPrice.productCode === productCode).map((res) => res.price.value)[0] ||
      0
    );
  }

  getDealerPriceData(dealerPrices: DealerPrice[], productCode: string): DealerPrice {
    return dealerPrices.filter((dealerPrice) => dealerPrice.productCode === productCode)[0];
  }

  getBasepriceBehavior$(): Observable<string> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.hide.baseprice.enabled'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.hide.baseprice.enabled')
              .map((res: any) => res.value)[0]
        )
      );
  }

  // Error handling
  errorHandl(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(errorMessage);
  }
}
